import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_carrier-connect-21/artifacts/svda2ys5_Concept%207%4072x%20%281%29.png";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/carrier-signup", label: "Carriers" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "py-3" : "py-5"
            }`}
        >
            <div
                className={`mx-auto max-w-7xl px-5 md:px-8 transition-all duration-300 ${
                    scrolled ? "" : ""
                }`}
            >
                <div
                    className={`flex items-center justify-between rounded-full px-4 md:px-6 py-2.5 transition-all duration-300 ${
                        scrolled
                            ? "glass border border-gray-200"
                            : "bg-transparent"
                    }`}
                >
                    <Link
                        to="/"
                        data-testid="navbar-logo"
                        className="flex items-center gap-2 group"
                    >
                        <img
                            src={LOGO_URL}
                            alt="Nexship"
                            className="h-9 md:h-10 w-auto"
                        />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-4 py-2 text-sm rounded-full transition-all ${
                                        isActive
                                            ? "text-brand-green"
                                            : "text-gray-700 hover:text-[#1a1a1a]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center">
                        <Link
                            to="/request-transport"
                            data-testid="nav-cta-request"
                            className="btn-primary text-sm"
                        >
                            Request Transport <ArrowUpRight size={16} />
                        </Link>
                    </div>

                    <button
                        data-testid="navbar-menu-toggle"
                        className="md:hidden text-[#1a1a1a]"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {open && (
                    <div
                        data-testid="mobile-menu"
                        className="md:hidden mt-3 glass rounded-2xl p-5 border border-gray-200 animate-fade-up"
                    >
                        <div className="flex flex-col gap-2">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    end={l.to === "/"}
                                    className={({ isActive }) =>
                                        `px-3 py-2 rounded-lg text-sm ${
                                            isActive
                                                ? "text-brand-green bg-gray-50"
                                                : "text-gray-800"
                                        }`
                                    }
                                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/request-transport"
                                className="btn-primary justify-center mt-2"
                                data-testid="mobile-nav-cta"
                            >
                                Request Transport
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};

export default Navbar;
